// src/App.js
import React, { useState, useEffect, useCallback } from 'react';
import CowTrackerDashboard from './CowTrackerDashboard'; // Your existing dashboard
import LoginForm from './LoginForm'; // The login form component

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://buzzcam.media.mit.edu/api'; // Make sure backend URL is correct

const App = () => {
  const [authToken, setAuthToken] = useState(localStorage.getItem('authToken'));
  const [currentUser, setCurrentUser] = useState(null); // e.g., { username: 'admin' }
  const [isLoadingAuth, setIsLoadingAuth] = useState(true);
  const [loginError, setLoginError] = useState('');

  const verifyToken = useCallback(async (tokenToVerify) => {
    if (!tokenToVerify) {
      setIsLoadingAuth(false);
      setCurrentUser(null);
      setAuthToken(null);
      localStorage.removeItem('authToken');
      return;
    }
    try {
      const response = await fetch(`${API_BASE_URL}/verify`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${tokenToVerify}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setCurrentUser({ username: data.user }); // Assuming backend sends { valid: true, user: 'username' }
        setAuthToken(tokenToVerify); // Ensure token state is updated if it was passed directly
        localStorage.setItem('authToken', tokenToVerify); // Refresh in case it's a new valid token
      } else {
        localStorage.removeItem('authToken');
        setAuthToken(null);
        setCurrentUser(null);
        if (response.status === 401 || response.status === 403) {
            console.log("Token verification failed, session expired or invalid.");
        }
      }
    } catch (error) {
      console.error('Error verifying token:', error);
      localStorage.removeItem('authToken');
      setAuthToken(null);
      setCurrentUser(null);
    } finally {
      setIsLoadingAuth(false);
    }
  }, []);

  useEffect(() => {
    const storedToken = localStorage.getItem('authToken');
    if (storedToken) {
      verifyToken(storedToken);
    } else {
      setIsLoadingAuth(false); // No token, no need to verify
    }
  }, [verifyToken]);

  const handleLogin = async (username, password) => {
    setLoginError('');
    try {
      const response = await fetch(`${API_BASE_URL}/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });
      const data = await response.json();
      if (response.ok && data.accessToken) {
        localStorage.setItem('authToken', data.accessToken);
        setAuthToken(data.accessToken);
        // Optionally, you can call /verify here to get user details if /login doesn't return them
        // For this backend, /login doesn't return user details other than what's in the token.
        // We can decode the token for the username or verify it.
        // For simplicity, we'll re-verify or just set a basic user object.
        // Assuming the JWT payload contains { username: '...' }
        const decodedToken = JSON.parse(atob(data.accessToken.split('.')[1])); // Basic decode, not for verification
        setCurrentUser({ username: decodedToken.username });
        setIsLoadingAuth(false); // Ensure loading state is false after login
        return true;
      } else {
        setLoginError(data.error || 'Login failed. Please try again.');
        return false;
      }
    } catch (error) {
      console.error('Login error:', error);
      setLoginError('An error occurred during login. Please try again.');
      return false;
    }
  };

  const handleLogout = async () => {
    // Optional: Call backend logout if it did more than just client-side removal
    // await fetch(`${API_BASE_URL}/logout`, { method: 'POST', headers: { 'Authorization': `Bearer ${authToken}` } });
    localStorage.removeItem('authToken');
    setAuthToken(null);
    setCurrentUser(null);
    setLoginError(''); // Clear any login errors on logout
    // No need to call setIsLoadingAuth(true) here as it's an explicit logout.
  };

  if (isLoadingAuth) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#0f172a', color: '#e2e8f0' }}>
        Loading Dashboard...
      </div>
    );
  }

  if (!authToken || !currentUser) {
    return <LoginForm onLogin={handleLogin} error={loginError} />;
  }

  // Pass authToken and currentUser to CowTrackerDashboard
  return <CowTrackerDashboard authToken={authToken} user={currentUser} onLogout={handleLogout} />;
};

export default App;
